import React, { useCallback, useContext, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

import {
  ResponsiveChartContainer,
  ChartsGrid,
  ChartsXAxis,
  ChartsYAxis,
  ChartsAxisHighlight,
  BarPlot,
  LinePlot,
  BarSeriesType,
} from "@mui/x-charts";

import { DatasetType } from "@mui/x-charts/internals";

import CustomLegend from "../CustomLegend";
import CustomAxisTooltip from "../CustomTooltip";
import NavigationTooltip from "../NavigableTooltip";

import { Bucket } from "../../../../../api/indicators";
import { UserContext } from "../../../../../context/UserContext";
import { yAxisFormatter } from "../../../dashboardHelpers";
import { useBrowserInfo } from "../../../../../helpers/useBrowserInfo";

interface Props {
  bucket: Bucket;
  series: BarSeriesType[];
  data: DatasetType;
  height: number;
  leftAxisLabel: string;
  rightAxisLabel?: string;
  currency?: string;
}

const DoubleBarChartBase = ({
  bucket,
  series,
  data,
  height,
  leftAxisLabel,
  rightAxisLabel,
  currency
}: Props) => {

  const theme = useTheme();
  const { locale } = useContext(UserContext);

  const [showNavigableLegend, setShowNavigableLegend] = useState(false);
  const [dataIndex, setDataIndex] = useState(0);

  const safari = useBrowserInfo().browser === 'Safari Browser';

  const normalizedData = useMemo(
    () =>
      (data as Record<string, unknown>[])?.map((item) => {
        const raw = item.dateTime;

        let parsed: Date | null = null;

        if (raw instanceof Date) {
          parsed = raw;
        } else if (raw != null) {
          const candidate = new Date(String(raw).replace(" ", "T"));

          if (!Number.isNaN(candidate.getTime())) {
            parsed = candidate;
          }
        }

        return {
          ...item,
          dateTime: parsed
        };
      }),
    [data]
  );

  const adaptedSeries = useMemo(() => {
    if (!safari) {
      return series;
    }

    return series.map((s) => ({
      ...s,
      type: "line" as const,
      showMark: true,
      curve: "linear" as const,
      markSize: 5
    }));
  }, [series, safari]);

  const formatAxisValue = useCallback((value: Date) => {
    switch (bucket) {
      case "hour":
        return new Intl.DateTimeFormat(locale, {
          hour: "2-digit",
          minute: "2-digit"
        }).format(value);

      case "day":
        return new Intl.DateTimeFormat(locale, {
          weekday: "short",
          day: "2-digit",
          month: "short"
        }).format(value);

      case "week":
        return new Intl.DateTimeFormat(locale, {
          day: "2-digit",
          month: "short"
        }).format(value);

      default:
        return new Intl.DateTimeFormat(locale).format(value);
    }
  }, [bucket, locale]);

  return (
    <>
      <Stack sx={{ width: "100%", mt: 2 }}>

        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography style={theme.typography.lineBarText}>
            {leftAxisLabel}
          </Typography>

          <Typography style={theme.typography.lineBarText}>
            {rightAxisLabel}
          </Typography>
        </Box>

        <Box
          onClick={() => setShowNavigableLegend(true)}
          sx={{
            width: "100%",
            ".MuiChartsAxisHighlight-root": {
              strokeDasharray: "none"
            }
          }}
        >
          <ResponsiveChartContainer
            dataset={normalizedData}
            series={adaptedSeries}
            height={height * 0.7}
            margin={{ top: 16, bottom: 32 }}
            onHighlightChange={(el) => {
              if (el?.dataIndex !== undefined) {
                setDataIndex(el.dataIndex);
              }
            }}
            xAxis={[
              {
                id: "x-axis",
                dataKey: "dateTime",
                scaleType: "band",
                valueFormatter: (v: Date) => {
                  if (!v) {
                    return "";
                  }

                  return formatAxisValue(v);
                }
              }
            ]}
            yAxis={[
              {
                id: "y-axis-left-id",
                scaleType: "linear",
                valueFormatter: yAxisFormatter
              },
              {
                id: "y-axis-right-id",
                scaleType: "linear",
                position: "right",
                min: 0,
                valueFormatter: yAxisFormatter
              }
            ]}
          >
            <ChartsGrid horizontal />

            {safari ? <LinePlot /> : <BarPlot />}

            <CustomAxisTooltip />

            <ChartsAxisHighlight x="line" />

            <ChartsXAxis axisId="x-axis" />
            <ChartsYAxis axisId="y-axis-left-id" />
            <ChartsYAxis axisId="y-axis-right-id" />

            <NavigationTooltip
              open={showNavigableLegend}
              onClose={() => setShowNavigableLegend(false)}
              dataset={normalizedData}
              series={adaptedSeries}
              seriesIndex={dataIndex}
              currency={currency}
            />
          </ResponsiveChartContainer>
          
        </Box>
      </Stack>

      <CustomLegend
        series={series}
        rightAlignedIds={["volume"]}
      />
    </>
  );
};

export default DoubleBarChartBase;
